import styles from './pendingPaymentList.module.css'
import { Badge, BooleanField, Column, FieldType, FieldValue, O, SpinnerDiv, Table, UIPermission, useLocalStorageActions } from "../../../../features/ui"
import { useMemo, useState } from "react"
import { NO_SUPPLIER_LABEL, PurchaseOrder, Supplier } from "../../../purchases"
import { PendingPaymentFilters } from "./pendingPaymentFilters"
import { Data, Labels, useDataTypesHooks, useDescribeHooks } from "../../../../features/data-types"
import { DataType, TableName } from "../../../../tables"
import { define } from "../../../../utils/typeUtils"
import { useNavigate } from 'react-router-dom'
import { RoutePath, useRoutesHooks } from '../../../../features/routes'
import { useSystemStateHooks } from '../../../system'
import { useFind } from '../../../../state/reducers/hooks'
import { firstItem } from '../../../../utils/listUtils'
import { useAdministrationStateActions } from '../../hooks/administrationHooks'
import { Employee, Expense, Ticket, TicketType } from '../../state/types'
import { useTicketHooks } from '../../hooks/ticketHooks'
import { operationHelpers } from '../../../operations'

type Props = {
    suppliers: Supplier[]
    tickets: Ticket[]
    parents: (PurchaseOrder | Expense)[]
}

export const PendingPaymentList = (props: Props) => {
    const { suppliers, tickets, parents } = props

    const [rows, setRows] = useState<Data[]>([])
    const [selectedIds, setSelectedIds] = useState<string[]>([])
    const [asyncDataLoaded, setAsyncDataLoaded] = useState(false)
    const [supplier, setSupplier] = useState<Supplier>()
    
    const { describeCode } = operationHelpers
    const { userHasPermission } = useSystemStateHooks()
    const readOnly = !userHasPermission(UIPermission.SAVE_PAYMENT_ORDERS)
    const { getTicketPendingAmount, isPurchase, findParent } = useTicketHooks()
    const navigate = useNavigate()
    const { getRoutePath } = useRoutesHooks()
    const { describe } = useDescribeHooks()
    const { formatNumber, formatPrice } = useDataTypesHooks()
    const find = useFind()
    const { selectTickets } = useAdministrationStateActions()
    const { saveSelectedTicketsData } = useLocalStorageActions()

    const firstRow = (ids: string[]) => rows.find(row => row.id === firstItem(ids))
    const selectedSupplierId = useMemo(() => firstRow(selectedIds)?.supplierId as O<string>, [selectedIds])
    const selectedIsSalary = useMemo(() => firstRow(selectedIds)?.type === TicketType.SALARY, [selectedIds])
    const selectedRefundEmployeeId = useMemo(() => firstRow(selectedIds)?.refundEmployeeId, [selectedIds])

    const columns: Column[] = [
        {
            name: 'ticket',
            type: FieldType.TEXT,
            label: 'Factura'
        },
        {
            name: 'operation',
            type: FieldType.TEXT,
            label: 'Operación'
        },
        {
            name: 'type',
            type: FieldType.TEXT,
            label: 'Tipo',
            render: (value?: FieldValue) => <Badge label={Labels.ticketTypeLabel(define(value) as TicketType)} />,
            sortValue: (value?: FieldValue) => value ? Labels.ticketTypeLabel(value as TicketType) : ''
        },
        {
            name: 'supplierId',
            type: FieldType.TABLE,
            table: TableName.SUPPLIERS,
            label: 'Proveedor',
            render: (value?: FieldValue) => {
                const supplier = find(TableName.SUPPLIERS, value) as O<Supplier>
                return <Badge label={supplier?.name || NO_SUPPLIER_LABEL} />
            },
            sortValue: (value?: FieldValue) => (find(TableName.SUPPLIERS, value) as O<Supplier>)?.name ?? NO_SUPPLIER_LABEL
        },
        {
            name: 'refundEmployeeId',
            type: FieldType.TABLE,
            table: TableName.EMPLOYEES,
            label: 'Empleado a Reintegrar',
            render: (value?: FieldValue) => {
                const employee = find(TableName.EMPLOYEES, value) as O<Employee>
                return employee?.name || <Badge label="No aplica" />
            },
            sortValue: (value?: FieldValue) => (find(TableName.EMPLOYEES, value) as O<Employee>)?.name ?? 'No aplica'
        },
        {
            name: 'creationDate',
            type: FieldType.DATE,
            label: 'Fecha de Creación'
        },
        {
            name: 'dueDate',
            type: FieldType.DATE,
            label: 'Fecha de Vto.'
        },
        {
            name: 'pendingAmount',
            type: FieldType.PRICE,
            label: 'Monto Pendiente'
        }
    ]

    const setRowsState = (ticketList: Ticket[]) => {
        const newRows = ticketList.map(ticket => {
            const code = describeCode(ticket)
            const table = ticket.parentTable as TableName
            const parent = define(findParent(table, ticket.parentId))
            const isPurchaseTicket = isPurchase(table)
            const operation = describe(table, parent)
            const refundEmployeeId = !isPurchaseTicket ? (parent as Expense).refundEmployeeId : undefined
            const pendingAmount = getTicketPendingAmount(ticket)
            
            return {
                id: ticket.id,
                ticket: code,
                operation,
                type: ticket.type,
                supplierId: parent.supplierId,
                refundEmployeeId: refundEmployeeId,
                creationDate: ticket.creationDate,
                dueDate: ticket.dueDate,
                pendingAmount
            }
        })
        setRows(newRows)
        setAsyncDataLoaded(true)
    }

    const onChangeSupplierId = (value?: string) => {
        const supplier = suppliers.find(supplier => supplier.id === value)
        setSupplier(supplier)
    }

    const onSearchStart = () => {
        setSelectedIds([])
        setAsyncDataLoaded(false)
    }

    const onSearchDone = (dataTypes: DataType[]) => {
        setRowsState(dataTypes as Ticket[])
    }

    const renderAsync = (element: JSX.Element) => asyncDataLoaded ? element : <SpinnerDiv />

    const onToggleCheck = (id: string) => (value?: boolean) => {
        setSelectedIds(prevIds => value ? prevIds.concat(id) : prevIds.filter(prevId => prevId !== id))
    }

    const renderActions = (id: string) => {
        const noneSelected = selectedIds.length === 0
        const row = rows.find(row => row.id === id)
        const enabled = noneSelected || (
            row?.supplierId === selectedSupplierId &&
            selectedIsSalary == (row?.type === TicketType.SALARY) &&
            row?.refundEmployeeId === selectedRefundEmployeeId
        )

        return <div className={styles.checkContainer}>
            {!readOnly && enabled ?
                <BooleanField
                    id={`check-${row?.id}`}
                    onChange={onToggleCheck(id)}
                    className={styles.checkField}
                /> : <></>
            }
        </div>
    }

    const onCreate = () => {
        selectTickets(selectedIds.map(id => define(find(TableName.TICKETS, id)) as Ticket))
        saveSelectedTicketsData(selectedIds)
        navigate(getRoutePath(RoutePath.PAYMENT_ORDERS, RoutePath.CREATE))
    }

    return (
        <>
            <PendingPaymentFilters
                tickets={tickets}
                parents={parents}
                onChangeSupplierId={onChangeSupplierId}
                onSearchStart={onSearchStart}
                onSearchDone={onSearchDone}
            />
            {supplier && (
                <div className={styles.infoContainer}>
                    <div className={styles.infoField}>
                        <span>Proveedor</span>
                        <span>{supplier.name}</span>
                    </div>
                    <div className={styles.infoField}>
                        <span>Facturas</span>
                        <span>{formatNumber(rows.length)}</span>
                    </div>
                    <div className={styles.infoField}>
                        <span>Deuda Total</span>
                        <span>{formatPrice(rows.reduce((acc, row) => acc + (row.pendingAmount! as number), 0))}</span>
                    </div>
                </div>
            )}
            {renderAsync(
                <Table
                    columns={columns}
                    rows={rows}
                    rowId="id"
                    createLabel="Crear Orden de Pago"
                    disableCreate={selectedIds.length === 0}
                    renderActions={renderActions}
                    readOnly={readOnly}
                    onCreate={onCreate}
                />
            )}
        </>
    )
}
